import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Modal,
  Box,
  TextField,
  Button,
  Stack,
  CircularProgress
} from '@mui/material';
import baseURL from '../../config';
import Navbar from '../../Navbar/Navbar';

const CharteredVessel = () => {
  const [charteredDetails, setCharteredDetails] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openStartModal, setOpenStartModal] = useState(false);
  const [openOffhireModal, setOpenOffhireModal] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [offhireDate, setOffhireDate] = useState('');
  const [perDatRate, setPerDatRate] = useState('');

  useEffect(() => {
    const fetchCharteredVessels = async () => {
      try {
        const response = await axios.get(`${baseURL}/api/chartered/getChartedVessel`);
        if (response.data.success) {
          setCharteredDetails(response.data.charteredDetails);
        }
      } catch (error) {
        console.error('Error fetching chartered vessels:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCharteredVessels();
  }, []);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const handleOpenStartModal = (id) => {
    setSelectedId(id);
    setOpenStartModal(true);
  };

  const handleOpenOffhireModal = (id) => {
    setSelectedId(id);
    setOpenOffhireModal(true);
  };

  const handleCloseModals = () => {
    setOpenStartModal(false);
    setOpenOffhireModal(false);
    setStartDate('');
    setOffhireDate('');
    setPerDatRate('');
    setSelectedId(null);
  };

  const handleSubmitStart = async () => {
    try {
      await axios.post(`${baseURL}/api/chartered/createCharteredVessel/${selectedId}`, {
        charterStartDate: [startDate],
        perDatRate: perDatRate ? Number(perDatRate) : undefined
      });
      alert("Start date and rate added!");
      handleCloseModals();
      window.location.reload();
    } catch (err) {
      console.error("Error adding start date and rate:", err);
      alert("Failed to add start date.");
    }
  };

  const handleSubmitOffhire = async () => {
    try {
      await axios.post(`${baseURL}/api/chartered/createCharteredVessel/${selectedId}`, {
        OffHireDate: [offhireDate]
      });
      alert("Offhire date added!");
      handleCloseModals();
      window.location.reload();
    } catch (err) {
      console.error("Error adding offhire date:", err);
      alert("Failed to add offhire date.");
    }
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', marginTop: '2rem' }}>
        <CircularProgress />
        <Typography variant="body2" mt={2}>Fetching data...</Typography>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <div className="p-4">
        <Typography variant="h5" gutterBottom>Chartered Vessels</Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Vessel</strong></TableCell>
                <TableCell><strong>Created Date</strong></TableCell>
                <TableCell><strong>Start Date(s)</strong></TableCell>
                <TableCell><strong>Offhire Date(s)</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
                <TableCell><strong>Per Day Rate</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {charteredDetails.map((item) => (
                <TableRow key={item._id} hover>
                  <TableCell>{item?.vesselName || 'N/A'}</TableCell>
                  <TableCell>{formatDate(item.createdAt)}</TableCell>

                  <TableCell>
                    {item.charterStartDate?.length > 0 ? (
                      item.charterStartDate.map((date, idx) => (
                        <Typography variant="body2" key={idx}>• {formatDate(date)}</Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    {item.OffHireDate?.length > 0 ? (
                      item.OffHireDate.map((date, idx) => (
                        <Typography variant="body2" key={idx}>• {formatDate(date)}</Typography>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>

                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="contained"
                        size="small"
                        color="primary"
                        onClick={() => handleOpenStartModal(item._id)}
                      >
                        Start Date
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={() => handleOpenOffhireModal(item._id)}
                      >
                        Offhire Date
                      </Button>
                    </Stack>
                  </TableCell>

                  <TableCell>
                    ₹ {item.perDatRate ? item.perDatRate : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Start Date Modal */}
        <Modal open={openStartModal} onClose={handleCloseModals}>
          <Box sx={modalStyles}>
            <Typography variant="h6" mb={2}>Set Start Date</Typography>
            <TextField
              label="Start Date"
              type="date"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <TextField
              label="Per Day Rate"
              type="number"
              fullWidth
              value={perDatRate}
              onChange={(e) => setPerDatRate(e.target.value)}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" fullWidth onClick={handleSubmitStart}>
              Submit
            </Button>
          </Box>
        </Modal>

        {/* Offhire Date Modal */}
        <Modal open={openOffhireModal} onClose={handleCloseModals}>
          <Box sx={modalStyles}>
            <Typography variant="h6" mb={2}>Set Offhire Date</Typography>
            <TextField
              label="Offhire Date"
              type="date"
              fullWidth
              value={offhireDate}
              onChange={(e) => setOffhireDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2 }}
            />
            <Button variant="contained" fullWidth onClick={handleSubmitOffhire}>
              Submit
            </Button>
          </Box>
        </Modal>
      </div>
    </>
  );
};

const modalStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
  borderRadius: 2
};

export default CharteredVessel;